import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import FilterSlider from "./FilterSlider";
import { useTheme } from "@mui/material/styles";

const amenitiesOptions = ["Wi-Fi", "Laundry", "Parking", "Furnished", "AC", "Gym"];

const selectSx = {
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.08)",
  borderRadius: "6px",
  fontSize: "0.85rem",
  "& .MuiSelect-icon": { color: "#fff" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.3)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.6)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#fff",
  },
};

const labelSx = {
  color: "#fff",
  fontSize: "0.8rem",
  mb: 0.5,
  display: "block",
};

const FilterSidebar = ({ onApply, onClear }) => {
  const theme = useTheme();
  const { control, handleSubmit, reset } = useForm();

  const menuProps = {
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    PaperProps: {
      sx: {
        backgroundColor: "#fff",
        color: "#212121",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        mt: 0.5,
        maxHeight: "180px",
        overflowY: "auto",
        "& .MuiList-root": { py: 0.5 },
        "& .MuiMenuItem-root": {
          fontSize: "0.875rem",
          color: "#212121",
          py: 0.75,
          "&:hover": { backgroundColor: "#f5f5f5" },
          "&.Mui-selected": {
            backgroundColor: "#001d3d",
            color: "#fff",
            "&:hover": { backgroundColor: "#002d5a" },
          },
        },
      },
    },
  };

  const onSubmit = (data) => {
    onApply(data);
  };

  const handleClear = () => {
    reset();
    onClear();
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "8px",
        backgroundColor: "#001d3d",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#fff" }}>
        Filter Rooms
      </Typography>
      <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.2)" }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Location */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={labelSx}>Location</Typography>
          <FormControl fullWidth size="small">
            <Controller
              name="city"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  {...field}
                  displayEmpty
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All Cities</MenuItem>
                  <MenuItem value="pune">Pune</MenuItem>
                  <MenuItem value="delhi">Delhi</MenuItem>
                  <MenuItem value="mumbai">Mumbai</MenuItem>
                  <MenuItem value="goa">Goa</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        </Box>

        {/* Distance */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={labelSx}>Distance (km)</Typography>
          <Controller
            name="distance"
            control={control}
            defaultValue={5}
            render={({ field }) => (
              <FilterSlider
                {...field}
                min={1}
                max={20}
                step={1}
                marks={[
                  { value: 5, label: "5km" },
                  { value: 10, label: "10km" },
                  { value: 15, label: "15km" },
                  { value: 20, label: "20km" },
                ]}
              />
            )}
          />
        </Box>

        {/* Rent */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={labelSx}>Monthly Rent (₹)</Typography>
          <Controller
            name="monthlyRent"
            control={control}
            defaultValue={[2000, 10000]}
            render={({ field }) => (
              <FilterSlider
                {...field}
                min={2000}
                max={20000}
                step={500}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `₹${value}`}
                marks={[
                  { value: 5000, label: "5k" },
                  { value: 10000, label: "10k" },
                  { value: 15000, label: "15k" },
                  { value: 20000, label: "20k" },
                ]}
              />
            )}
          />
        </Box>

        {/* Roommates */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6}>
            <Typography sx={labelSx}>Current</Typography>
            <FormControl fullWidth size="small">
              <Controller
                name="currentRoommates"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    sx={selectSx}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4+</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={labelSx}>Needed</Typography>
            <FormControl fullWidth size="small">
              <Controller
                name="neededRoommates"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    sx={selectSx}
                    MenuProps={menuProps}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                    <MenuItem value={3}>3</MenuItem>
                    <MenuItem value={4}>4+</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>

        {/* Gender Preference */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={labelSx}>Gender Preference</Typography>
          <Controller
            name="gender"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel
                  value="female"
                  control={<Radio size="small" sx={{ color: "rgba(255,255,255,0.6)", "&.Mui-checked": { color: "#fff" } }} />}
                  label={<Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>Female</Typography>}
                />
                <FormControlLabel
                  value="male"
                  control={<Radio size="small" sx={{ color: "rgba(255,255,255,0.6)", "&.Mui-checked": { color: "#fff" } }} />}
                  label={<Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>Male</Typography>}
                />
                <FormControlLabel
                  value="any"
                  control={<Radio size="small" sx={{ color: "rgba(255,255,255,0.6)", "&.Mui-checked": { color: "#fff" } }} />}
                  label={<Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>Any</Typography>}
                />
              </RadioGroup>
            )}
          />
        </Box>

        {/* Amenities */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={labelSx}>Amenities</Typography>
          <FormControl fullWidth size="small">
            <Controller
              name="amenities"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  {...field}
                  multiple
                  displayEmpty
                  sx={selectSx}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>Select amenities</Typography>
                    ) : (
                      <Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>{selected.join(", ")}</Typography>
                    )
                  }
                  MenuProps={menuProps}
                >
                  {amenitiesOptions.map((amenity) => (
                    <MenuItem key={amenity} value={amenity}>
                      {amenity}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Box>

        {/* Status */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={labelSx}>Status</Typography>
          <Controller
            name="status"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel
                  value="available"
                  control={<Radio size="small" sx={{ color: "rgba(255,255,255,0.6)", "&.Mui-checked": { color: "#fff" } }} />}
                  label={<Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>Available</Typography>}
                />
                <FormControlLabel
                  value="soon"
                  control={<Radio size="small" sx={{ color: "rgba(255,255,255,0.6)", "&.Mui-checked": { color: "#fff" } }} />}
                  label={<Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>Coming Soon</Typography>}
                />
              </RadioGroup>
            )}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mb: 1.5, backgroundColor: "#fff", color: "#1a1a2e", fontWeight: 600, "&:hover": { backgroundColor: "#e0e0e0" } }}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleClear}
          sx={{ borderColor: "rgba(255,255,255,0.4)", color: "#fff", "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" } }}
        >
          Clear Filters
        </Button>
      </form>
    </Box>
  );
};

export default FilterSidebar;
